import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, LogIn } from "lucide-react";
import Seo from "../../components/Seo";
import Logo from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../api";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#070B14] bg-grid flex items-center justify-center px-4" data-testid="admin-login-page">
      <Seo title="Admin Login" description="Trendsight Media admin dashboard login." />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md glass-panel rounded-2xl p-9"
      >
        <Logo />
        <div className="mt-8 flex items-center gap-2.5 text-slate-400">
          <Lock size={15} className="text-blue-400" />
          <p className="text-sm">Restricted area. Authorized administrators only.</p>
        </div>
        <form onSubmit={submit} className="mt-7 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-2" htmlFor="a-email">Email</label>
            <input
              id="a-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="w-full rounded-xl bg-slate-950/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none px-4 py-3 text-sm text-slate-200 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-2" htmlFor="a-password">Password</label>
            <input
              id="a-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="w-full rounded-xl bg-slate-950/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none px-4 py-3 text-sm text-slate-200 transition-colors"
            />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold px-8 py-3.5 transition-all duration-300 active:scale-[0.98]"
          >
            {loading ? "Signing in..." : "Sign In"} <LogIn size={16} />
          </button>
        </form>
      </motion.div>
    </main>
  );
}
